/**
 * Platform Admin API Helpers
 * 
 * Shared utilities for API communication in platform admin features.
 * Centralizes API base URL construction and authentication header generation.
 * 
 * IMPORTANT: getApiBase() delegates to the canonical getBaseUrl() from
 * shared/utils/apiConfig.ts to ensure consistent URL resolution across
 * all environments (localhost, dev-brain, prospance-brain, admin).
 */

import { getBaseUrl } from '../../../shared/utils/apiConfig';

/**
 * Get the API base URL based on current environment.
 * Delegates to the canonical getBaseUrl() for consistent behavior.
 * 
 * @returns API base URL (e.g. http://localhost:8000 or https://dev-brain.astrapeak.ai)
 */
export const getApiBase = (): string => {
  return getBaseUrl();
};

/**
 * Get authentication token from localStorage.
 * 
 * @returns Auth token or empty string if not found
 */
export const getAuthToken = (): string => {
  return localStorage.getItem('astrapeak_auth_token') || '';
};

/**
 * Get standard authentication headers for API requests.
 * 
 * @returns Headers object with Authorization header
 */
export const getAuthHeaders = (): Record<string, string> => {
  return {
    'Authorization': `Bearer ${getAuthToken()}`
  };
};

/**
 * Get headers for JSON API requests (includes Content-Type).
 * 
 * @returns Headers object with Authorization and Content-Type
 */
export const getJsonHeaders = (): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getAuthToken()}`
  };
};
